import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { checkoutActions } from '@detox/actions';
import { navigate } from 'gatsby';

import {
  getFormConfigForNewNewFlow,
  getItemSkuToOrderIdMapping,
  updateCustomer
} from '../helper';

import ORDER_CONSTANT from '../../../constants/order';
import { navigation } from '../../../middlewares/navigation-constants';

import isSimOnlyPlan from '../../../helpers/is-sim-only-plan';
import { KeyValue } from '../../../types/common.types';
import { getCartError, getCartErrorDataModel } from '../../../helpers/cart';
import {
  BillingPrefType,
  FormConfig
} from '../../../types/registrationCheckout';
import isFeatureFlagEnabled from '../../../helpers/feature-flags';
import { FEATURE_FLAG_ALLOW_SUBSEQUENT_ALLOCATE_API } from '../../../types/featureFlag.types';
import { AbFeatureNames } from '../../../constants/ab-feature-names';
import { useABTesting } from '@wec-core/ab-testing';

interface RootState {
  checkout: KeyValue;
  cart: KeyValue;
  plan: KeyValue;
  order: KeyValue;
  fulfillment: KeyValue;
  product: KeyValue;
  user: KeyValue;
  ekyc: KeyValue;
}

interface CheckoutHelperReturnType {
  handleOnFormSubmit: (values: KeyValue, passValues: KeyValue) => Promise<void>;
}

interface CheckoutHelperInputsType {
  onErrorScenarioFound: (cartErrorDataModel: KeyValue) => void;
  errorScenariosData: KeyValue[];
  onFormSubmit?: (values: KeyValue, path: string) => void;
  goToOrderSummary: () => void;
  billingPrefData: BillingPrefType[];
  onGettingFormConfigs: (formConfigs: FormConfig) => void;
}

export const useCheckOutHelper = ({
  onErrorScenarioFound,
  errorScenariosData,
  onFormSubmit,
  goToOrderSummary,
  billingPrefData,
  onGettingFormConfigs
}: CheckoutHelperInputsType): CheckoutHelperReturnType => {
  const {
    checkout,
    cart,
    selectedPlan,
    order,
    addressInfo = {},
    selectedProduct,
    cisUser,
    userInformation,
    ekycState
  } = useSelector((state: RootState) => {
    return {
      checkout: state.checkout,
      cart: state.cart,
      selectedPlan: state.plan?.selectedPlan,
      order: state.order,
      addressInfo: state.fulfillment?.addressInfo,
      selectedProduct: state.product?.selectedProduct,
      cisUser: state.user?.cis?.information,
      userInformation: state.user?.information,
      ekycState: state.ekyc
    };
  });
  const dispatch = useDispatch();
  const { isFeatureAllowed: abTestDeletePendingOrderForNewFlow } = useABTesting(
    {
      featureName: AbFeatureNames.DELETE_PENDING_ORDER_FOR_NEW_FLOW
    }
  );
  const verifyEkycMcssPayload = ekycState?.verifyEkycPayload || {};
  const { productOrder, addedToContact } = order;

  const showValidateError = useCallback(() => {
    const errorScenarioData = getCartError(
      cart?.checkoutErrorData,
      errorScenariosData
    );
    const cartErrorDataModel = getCartErrorDataModel(
      errorScenarioData,
      selectedPlan?.planName
    );
    onErrorScenarioFound(cartErrorDataModel);
  }, [
    cart.checkoutErrorData,
    errorScenariosData,
    onErrorScenarioFound,
    selectedPlan.planName
  ]);

  const getFlowConfigs = () => {
    const device = cart?.cartOrder?.device;
    const hasAccessories = cart?.cartOrder?.accessories?.length > 0;
    const simType = cart?.order?.newlyAddedSimDetails?.simDetails?.simType;

    return getFormConfigForNewNewFlow({
      device,
      isCis: cisUser && !cisUser.$myInfo,
      isNric: false, // does not matter as we need only eSim type
      isNricSilver: false,
      hasAccessories,
      simType
    });
  };

  const proceedToNextPage = useCallback(async () => {
    if (isFeatureFlagEnabled(FEATURE_FLAG_ALLOW_SUBSEQUENT_ALLOCATE_API)) {
      await checkoutActions.allocateEquipmentForAvailableProducts({
        cart,
        simOptions: productOrder?.sim,
        dispatch,
        productOrderId: productOrder?.productOrderId,
        notAvailableSkus: [],
        itemSkuToOrderIdMapping: getItemSkuToOrderIdMapping(
          selectedProduct?.deviceCode,
          productOrder?.productOrderItemId
        )
      });
    }
    dispatch(checkoutActions.resetCreateCustomerStatus());

    // Simonly order should go to order summary
    const flowConfig: KeyValue = getFlowConfigs();
    if (flowConfig.byPassFulfilment) {
      goToOrderSummary();
    } else {
      navigate(`/${navigation.ORDER_FULFILMENT_PAGE}`);
    }
  }, [cart, dispatch, productOrder.productOrderId, productOrder.sim]);

  useEffect(() => {
    const flowConfig: KeyValue = getFlowConfigs();

    if (onGettingFormConfigs) {
      onGettingFormConfigs({
        hasBillingPref: flowConfig.showBillingPref,
        hasUploadID: flowConfig.showStaffUploadDocument,
        byPassFulfilment: flowConfig.byPassFulfilment
      });
    }
    dispatch(checkoutActions.resetCreateCustomerStatus());
  }, []);

  useEffect(() => {
    if (checkout?.customerCreated) {
      const hasError =
        cart?.checkoutErrorData &&
        cart?.checkoutOrderError &&
        cart?.checkoutErrorScenarioCheckCompleted;
      const finishedWithoutError =
        cart?.checkoutErrorScenarioCheckCompleted &&
        cart?.checkoutOrderError === false;

      if (hasError) {
        showValidateError();
      } else if (finishedWithoutError) {
        proceedToNextPage();
      }
    }
  }, [
    cart.checkoutErrorData,
    cart.checkoutErrorScenarioCheckCompleted,
    cart.checkoutOrderError,
    checkout.customerCreated,
    proceedToNextPage,
    showValidateError
  ]);

  const handleOnFormSubmit = useCallback(
    async (values, passValues): Promise<void> => {
      const params = {
        formValues: values,
        addressInfo: addressInfo?.address,
        passType: passValues.passType,
        passId: passValues.id,
        addedToContact,
        portIn: productOrder?.type === ORDER_CONSTANT.TYPE.PORTIN,
        productOrder,
        isSimOnlyIPP: isSimOnlyPlan(selectedPlan) && selectedProduct,
        dispatch
      };

      // In both existing new and new new, /information API would be similar post skip worry free feature
      const customerIdForUpdate =
        userInformation?.clientContext?.customers?.[0]?.customerId;

      if (
        (values?.customerExists ||
          (values?.verifyEkycMcssPayload?.contactExists &&
            values?.verifyEkycMcssPayload?.customerIds?.length)) &&
        verifyEkycMcssPayload?.contactId
      ) {
        const retrieveContactData = checkout?.checkoutRetrieveContactData;
        return await updateCustomer({
          ...params,
          contactId: verifyEkycMcssPayload?.contactId,
          customerId: customerIdForUpdate,
          selectedPlan,
          billingPrefData,
          myInfoDataComplete: true,
          shouldDeleteOpenOrder:
            order.productOrder?.type === ORDER_CONSTANT.TYPE.NEW &&
            abTestDeletePendingOrderForNewFlow,
          retrieveContactData
        });
      }
      proceedToNextPage();
    },
    [
      addedToContact,
      billingPrefData,
      addressInfo.address,
      dispatch,
      productOrder,
      selectedPlan,
      selectedProduct
    ]
  );

  return { handleOnFormSubmit };
};
